import { MINT_100, PINE_100 } from '@rategravity/own-up-component-library';
import React from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { displayNumber } from '../../common/utils';
import { MonthlyPayment } from '../types';

const formatCurrency = (tickItem: number) => {
  if (tickItem >= 1e6) {
    return `$${(tickItem / 1e6).toFixed(0)}M`;
  } else if (tickItem >= 1e3) {
    return `$${(tickItem / 1e3).toFixed(0)}k`;
  } else {
    return `$${tickItem}`;
  }
};

const fieldToLabel = {
  totalPayedA: 'Total Paid A',
  totalPayedB: 'Total Paid B'
};

const CustomTooltip = ({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: any;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ backgroundColor: '#fff', padding: '5px', border: '1px solid #ccc' }}>
        <strong className="label">{`Year: ${label}`}</strong>
        <div>{`${fieldToLabel[payload[0].name as keyof typeof fieldToLabel]}: ${displayNumber(payload[0].value)}`}</div>
        {payload[1] && (
          <div>{`${fieldToLabel[payload[1].name as keyof typeof fieldToLabel]}: ${displayNumber(payload[1].value)}`}</div>
        )}
      </div>
    );
  }

  return null;
};

export const MortgageLineChart = ({
  dataSet1,
  dataSet2
}: {
  dataSet1: MonthlyPayment[];
  dataSet2: MonthlyPayment[];
}) => {
  const monthlyPayment1 = dataSet1[0].monthlyBreakdown[0].monthlyPayment;
  const monthlyPayment2 = dataSet2[0].monthlyBreakdown[0].monthlyPayment;
  const maxDays = Math.max(dataSet1.length, dataSet2.length);
  const data: {
    year: string;
    totalPayedA: number | null;
    totalPayedB: number | null;
  }[] = [
    {
      year: `0`,
      totalPayedA: 0,
      totalPayedB: 0
    }
  ];
  for (let index = 0; index < maxDays; index++) {
    data.push({
      year: `${index + 1}`,
      totalPayedA:
        index < dataSet1.length
          ? monthlyPayment1 * 12 * index + monthlyPayment1 * dataSet1[index].monthlyBreakdown.length
          : null,
      totalPayedB:
        index < dataSet2.length
          ? monthlyPayment2 * 12 * index + monthlyPayment2 * dataSet2[index].monthlyBreakdown.length
          : null
    });
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart
        data={data}
        margin={{
          top: 5,
          bottom: 5
        }}
      >
        <XAxis
          dataKey="year"
          tickCount={5}
          tickLine={false}
          tickMargin={14}
          height={54}
          tickFormatter={(value, index) => (index % 5 === 0 ? value : '')}
        />
        <YAxis tickFormatter={(value: number) => formatCurrency(value)} />
        <Tooltip content={<CustomTooltip />} />
        <Line type="monotone" dataKey="totalPayedA" stroke={PINE_100} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="totalPayedB" stroke={MINT_100} dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
};
